import React from "react";
import { connect } from "react-redux";
import language from "../../language";
import donorsQuery from "../../utils/queries/donorsQuery";
import bloodCenterQuery from "../../utils/queries/bloodCenterQuery";
import bloodCenterSiteQuery from "../../utils/queries/bloodCenterSiteQuery";
import { Table } from "../Table";
import { Modal } from "../Modal";
import { Button } from "../Button";
import { Input, Select } from "../Input";
import { defineRoleCenterSite } from "../../utils/handleAccessRoles";

class BulkSearchDonors extends React.Component {
  state = {
    centerOptions: [],
    centerSiteOptions: [],
    selectedCenter: null,
    selectedCenterSite: null,
    startDate: "",
    endDate: "",
    category: "",
    isLoadingCenters: false,
    isLoadingCenterSites: false,
    bulkSearchData: [],
    bulkSearchCount: 0,
    bulkSearchPage: 1,
    bulkSearchLimit: 10,
    bulkSearchTotalPageCount: 0,
    bulkSearchModalSearchText: "",
    searchingBulkModal: false,
    showBulkSearchModal: false,
    isBulkSearching: false,
  };

  componentDidMount = async () => {
    await this.getCenters();
    await this.getCenterSites();
  };

  getCenters = async () => {
    try {
      const { defaultLanguage } = this.props;
      this.setState({ isLoadingCenters: true });
      const centers = await bloodCenterQuery(defaultLanguage, {
        page: 1,
        limit: 1000,
        type: "dropdown",
      });
      this.setState({
        centerOptions: centers || [],
        isLoadingCenters: false,
      });
    } catch (error) {
      this.setState({ isLoadingCenters: false });
    }
  };

  getCenterSites = async (centerId) => {
    try {
      const { defaultLanguage } = this.props;
      this.setState({ isLoadingCenterSites: true });
      const sites = await bloodCenterSiteQuery(defaultLanguage, {
        page: 1,
        limit: 1000,
        type: "dropdown",
        center: centerId ? [centerId] : undefined,
      });
      this.setState({
        centerSiteOptions: sites || [],
        isLoadingCenterSites: false,
      });
    } catch (error) {
      this.setState({ isLoadingCenterSites: false });
    }
  };

  onChangeText = (field, e) => {
    this.setState({
      [field]: e?.target?.value || "",
    });
  };

  onChangeCenter = async (selectedCenter) => {
    this.setState({
      selectedCenter,
      selectedCenterSite: null,
      centerSiteOptions: [],
    });
    await this.getCenterSites(selectedCenter?.value);
  };

  onChangeCenterSite = (selectedCenterSite) => {
    this.setState({
      selectedCenterSite,
    });
  };

  returnBulkHeaders = () => {
    const { defaultLanguage } = this.props;
    const lng = language[defaultLanguage];

    return [
      {
        title: lng.donor_number,
        key: "donorNumber",
        type: "link",
        onPress: (item) =>
          (window.location.href = `/dashboard/donors/${item._id}/${item.firstName} ${item.lastName}`),
      },
      { title: lng.first_name, key: "firstName" },
      { title: lng.last_name, key: "lastName" },
      { title: lng.category, key: "category" },
      { title: lng.phone_number, key: "phoneNumber" },
      { title: lng.email_address, key: "email" },
      { title: lng.center, key: "center.name" },
      { title: lng.center_site, key: "centerSite.name" },
    ];
  };

  fetchBulkSearchData = async (
    page = 1,
    showBulkSearchModal = true,
    searchDonorNumber,
  ) => {
    try {
      const { defaultLanguage } = this.props;
      const roleScope = (await defineRoleCenterSite()) || {};
      const { centerRole, centerSiteRole } = roleScope;
      const { bulkSearchLimit } = this.state;

      const donorNumberToSearch =
        typeof searchDonorNumber === "string"
          ? searchDonorNumber
          : this.state.bulkSearchModalSearchText;

      const params = {
        page,
        limit: bulkSearchLimit,
        center: this.state.selectedCenter?.value || centerRole || undefined,
        centerSite:
          this.state.selectedCenterSite?.value || centerSiteRole || undefined,
        donorNumber: donorNumberToSearch
          ? donorNumberToSearch.replace(/\s+/g, "")
          : undefined,
        startDate: this.state.startDate || undefined,
        endDate: this.state.endDate || undefined,
        category: this.state.category || undefined,
      };

      this.setState({ isBulkSearching: true });

      const response = await donorsQuery(defaultLanguage, params);
      const data = (response?.data || []).map((item) => ({
        ...item,
        fullName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
      }));
      const totalRecords =
        response?.totalRecords || response?.data?.length || data.length || 0;
      const bulkSearchTotalPageCount = response?.count || 0;

      this.setState({
        isBulkSearching: false,
        bulkSearchData: data,
        bulkSearchCount: totalRecords,
        bulkSearchPage: page,
        bulkSearchTotalPageCount,
        showBulkSearchModal,
      });
    } catch (error) {
      this.setState({ isBulkSearching: false });
    }
  };

  handleBulkSearch = async () => {
    this.setState(
      {
        bulkSearchPage: 1,
      },
      async () => {
        await this.fetchBulkSearchData(1, true);
      },
    );
  };

  handleBulkPagination(page) {
    this.setState(
      {
        bulkSearchPage: page,
      },
      () => {
        this.fetchBulkSearchData(page, true);
      },
    );
  }

  handleModalSearchChange = (e) => {
    this.setState({
      bulkSearchModalSearchText: e?.target?.value || "",
    });
  };

  handleModalDonorSearch = async () => {
    this.setState(
      {
        searchingBulkModal: true,
        bulkSearchPage: 1,
      },
      async () => {
        await this.fetchBulkSearchData(
          1,
          true,
          this.state.bulkSearchModalSearchText,
        );
        this.setState({ searchingBulkModal: false });
      },
    );
  };

  render() {
    const { defaultLanguage } = this.props;
    const lng = language[defaultLanguage];
    const categoryOptions = [
      { label: lng.all, value: "" },
      { label: lng.new_donors, value: "newDonor" },
      { label: lng.repeat_donors, value: "repeatDonor" },
      { label: lng.new_donors, value: "new" },
      { label: lng.regular_donors, value: "regular" },
      { label: lng.irregular_donors, value: "irregular" },
      { label: lng.dropped_donors, value: "dropped" },
    ];
    const bulkSearchTitle = `${lng.search} ${lng.donors}`;

    return (
      <>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <Select
                label={lng.center}
                options={this.state.centerOptions}
                value={this.state.selectedCenter}
                onChange={this.onChangeCenter}
                isLoading={this.state.isLoadingCenters}
              />
            </div>
            <div className="col-md-6">
              <Select
                label={lng.center_site}
                options={this.state.centerSiteOptions}
                value={this.state.selectedCenterSite}
                onChange={this.onChangeCenterSite}
                isLoading={this.state.isLoadingCenterSites}
                disabled={
                  this.state.isLoadingCenterSites ||
                  (this.state.selectedCenter &&
                    this.state.centerSiteOptions.length === 0)
                }
              />
            </div>
            <div className="col-md-6">
              <Input
                label={lng.start_date}
                type="date"
                value={this.state.startDate}
                onChange={(e) => this.onChangeText("startDate", e)}
              />
            </div>
            <div className="col-md-6">
              <Input
                label={lng.end_date}
                type="date"
                value={this.state.endDate}
                onChange={(e) => this.onChangeText("endDate", e)}
              />
            </div>
            <div className="col-md-12">
              <Select
                label={lng.category}
                options={categoryOptions}
                value={categoryOptions.find(
                  (opt) => opt.value === this.state.category,
                )}
                onChange={(option) =>
                  this.setState({ category: option?.value || "" })
                }
              />
            </div>
            <div className="col-md-12 mt-3">
              <Button
                text={lng.search}
                onPress={this.handleBulkSearch}
                isSubmitting={this.state.isBulkSearching}
              />
            </div>
          </div>
        </div>

        <Modal
          handleClose={() => this.setState({ showBulkSearchModal: false })}
          show={this.state.showBulkSearchModal}
          title={`${bulkSearchTitle} (${this.state.bulkSearchCount})`}
          size="lg"
        >
          <div className="card-body">
            <Table
              data={this.state.bulkSearchData}
              headers={this.returnBulkHeaders()}
              isSearch
              search_text={this.state.bulkSearchModalSearchText}
              handleSearch={this.handleModalSearchChange}
              addSearchButton
              searching={this.state.searchingBulkModal}
              handlePressSearch={this.handleModalDonorSearch}
              placeholder={lng.donor_number_placeholder}
              isLoading={this.state.isBulkSearching}
              actions={[]}
              page={this.state.bulkSearchPage}
              limit={this.state.bulkSearchLimit}
              totalPageCount={this.state.bulkSearchTotalPageCount}
              totalRecords={this.state.bulkSearchCount}
              handlePagination={this.handleBulkPagination.bind(this)}
            />
          </div>
        </Modal>
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(BulkSearchDonors);
